import React from 'react';
import { Box, Grid, Typography, Stack } from '@mui/material';
import { Link } from 'react-router-dom';
import PhoneIcon from '@mui/icons-material/Phone';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import "@fontsource/playfair-display";

export default function Footer() {
    return (
        <Box
            component="footer"
            sx={{
                backgroundColor: '#1a1a1a',
                color: '#ccc',
                pt: 6,
                pb: 3,
                mt: 'auto',
            }}
        >
            <Box sx={{ maxWidth: 1200, mx: 'auto', px: { xs: 3, md: 6 } }}>
                <Grid container spacing={4}>
                    {/* Brand */}
                    <Grid item xs={12} sm={6} md={3}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                            <Box
                                component="img"
                                src="/splash.png"
                                alt="Splash Beauty Lounge"
                                sx={{ height: 60 }}
                                onError={(e) => {
                                    e.target.style.display = 'none';
                                }}
                            />
                        </Box>
                        <Typography
                            sx={{
                                fontFamily: '"Lato", sans-serif',
                                fontSize: '0.85rem',
                                color: '#aaa',
                                lineHeight: 1.6,
                            }}
                        >
                            Elevate Your Look, Elevate Your Confidence
                        </Typography>
                    </Grid>

                    {/* Quick Links */}
                    <Grid item xs={12} sm={6} md={2}>
                        <Typography
                            sx={{
                                fontFamily: '"Lato", sans-serif',
                                fontWeight: 700,
                                fontSize: '0.9rem',
                                color: '#fff',
                                mb: 2,
                                letterSpacing: '0.05em',
                            }}
                        >
                            Quick Links
                        </Typography>
                        <Stack spacing={1}>
                            {[
                                { label: 'Home', path: '/' },
                                { label: "Our Services", path: '/services' },
                                { label: 'About Us', path: '/about' },
                                { label: 'Contact', path: '/contact' },
                            ].map((link) => (
                                <Typography
                                    key={link.label}
                                    component={Link}
                                    to={link.path}
                                    sx={{
                                        fontFamily: '"Lato", sans-serif',
                                        fontSize: '0.85rem',
                                        color: '#aaa',
                                        textDecoration: 'none',
                                        transition: 'color 0.2s',
                                        '&:hover': { color: '#FF0099' },
                                    }}
                                >
                                    {link.label}
                                </Typography>
                            ))}
                        </Stack>
                    </Grid>

                    {/* Services */}
                    <Grid item xs={12} sm={6} md={2}>
                        <Typography
                            sx={{
                                fontFamily: '"Lato", sans-serif',
                                fontWeight: 700,
                                fontSize: '0.9rem',
                                color: '#fff',
                                mb: 2,
                                letterSpacing: '0.05em',
                            }}
                        >
                            Explore
                        </Typography>
                        <Stack spacing={1}>
                            {[
                                { label: "Women's Services", path: '/services#women' },
                                { label: "Men's Services", path: '/services#men' },
                                { label: 'Skin & Hair', path: '/services#skin-hair' },
                                { label: 'Bridal Specials', path: '/services#bridal' },
                            ].map((service) => (
                                <Typography
                                    key={service.label}
                                    component={Link}
                                    to={service.path}
                                    sx={{
                                        fontFamily: '"Lato", sans-serif',
                                        fontSize: '0.85rem',
                                        color: '#aaa',
                                        textDecoration: 'none',
                                        transition: 'color 0.2s',
                                        '&:hover': { color: '#FF0099' },
                                    }}
                                >
                                    {service.label}
                                </Typography>
                            ))}
                        </Stack>
                    </Grid>

                    {/* Contact */}
                    <Grid item xs={12} sm={6} md={5}>
                        <Typography
                            sx={{
                                fontFamily: '"Lato", sans-serif',
                                fontWeight: 700,
                                fontSize: '0.9rem',
                                color: '#fff',
                                mb: 2,
                                letterSpacing: '0.05em',
                            }}
                        >
                            Our Branches & Contact
                        </Typography>
                        <Stack spacing={2.5}>
                            {/* Branch 1: Asilmetta */}
                            <Box>
                                <Typography sx={{ fontFamily: '"Playfair Display", serif', fontSize: '0.95rem', color: '#FF0099', fontWeight: 700, mb: 0.5 }}>
                                    Asilmetta Branch
                                </Typography>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                                    <PhoneIcon sx={{ color: '#FF0099', fontSize: '0.95rem' }} />
                                    <Typography
                                        component="a"
                                        href="tel:8977626223"
                                        sx={{ fontFamily: '"Lato", sans-serif', fontSize: '0.85rem', color: '#aaa', textDecoration: 'none', '&:hover': { color: '#FF0099' } }}
                                    >
                                        +91 89776 26223
                                    </Typography>
                                </Box>
                                <Box 
                                    component="a" 
                                    href="https://maps.app.goo.gl/7r93iVAVRdYumT5n9" 
                                    target="_blank" 
                                    rel="noopener noreferrer"
                                    sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, textDecoration: 'none' }}
                                >
                                    <LocationOnIcon sx={{ color: '#FF0099', fontSize: '0.95rem', mt: 0.2 }} />
                                    <Typography
                                        sx={{ fontFamily: '"Lato", sans-serif', fontSize: '0.85rem', color: '#aaa', lineHeight: 1.5, '&:hover': { color: '#FF0099' } }}
                                    >
                                        Opp. Bombay Gaslight Stores, Asilmetta Junction, Visakhapatnam
                                    </Typography>
                                </Box>
                            </Box>

                            {/* Branch 2: Madhurawada */}
                            <Box>
                                <Typography sx={{ fontFamily: '"Playfair Display", serif', fontSize: '0.95rem', color: '#FF0099', fontWeight: 700, mb: 0.5 }}>
                                    Madhurawada Branch
                                </Typography>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5, flexWrap: 'wrap' }}>
                                    <PhoneIcon sx={{ color: '#FF0099', fontSize: '0.95rem' }} />
                                    <Typography
                                        component="a"
                                        href="tel:9949788891"
                                        sx={{ fontFamily: '"Lato", sans-serif', fontSize: '0.85rem', color: '#aaa', textDecoration: 'none', '&:hover': { color: '#FF0099' } }}
                                    >
                                        +91 99497 88891
                                    </Typography>
                                    <Typography sx={{ color: '#666', fontSize: '0.85rem' }}>|</Typography>
                                    <Typography
                                        component="a"
                                        href="tel:9949388891"
                                        sx={{ fontFamily: '"Lato", sans-serif', fontSize: '0.85rem', color: '#aaa', textDecoration: 'none', '&:hover': { color: '#FF0099' } }}
                                    >
                                        +91 99493 88891
                                    </Typography>
                                </Box>
                                <Box 
                                    component="a" 
                                    href="https://share.google/V5E9UhEwGzjXTvJ0N" 
                                    target="_blank" 
                                    rel="noopener noreferrer"
                                    sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, textDecoration: 'none' }}
                                >
                                    <LocationOnIcon sx={{ color: '#FF0099', fontSize: '0.95rem', mt: 0.2 }} />
                                    <Typography
                                        sx={{ fontFamily: '"Lato", sans-serif', fontSize: '0.85rem', color: '#aaa', lineHeight: 1.5, '&:hover': { color: '#FF0099' } }}
                                    >
                                        Madhurawada, Visakhapatnam
                                    </Typography>
                                </Box>
                            </Box>

                            {/* Working Hours */}
                            <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, pt: 0.5 }}>
                                <AccessTimeIcon sx={{ color: '#FF0099', fontSize: '1rem', mt: 0.2 }} />
                                <Box>
                                    {[
                                        { day: 'Monday:', time: '9:00AM - 9:00PM' },
                                        { day: 'Tuesday:', time: '9:00AM - 9:00PM' },
                                        { day: 'Wednesday:', time: '9:00AM - 9:00PM' },
                                        { day: 'Thursday:', time: '9:00AM - 9:00PM' },
                                        { day: 'Friday:', time: '9:00AM - 9:00PM' },
                                        { day: 'Saturday:', time: '9:00AM - 9:00PM' },
                                        { day: 'Sunday:', time: '9:00AM - 9:00PM' }
                                    ].map((item) => (
                                        <Box key={item.day} sx={{ display: 'flex', gap: 1 }}>
                                            <Typography
                                                sx={{ 
                                                    fontFamily: '"Lato", sans-serif', 
                                                    fontSize: '0.8rem', 
                                                    color: '#aaa', 
                                                    width: { xs: '85px', md: '90px' },
                                                    flexShrink: 0
                                                }}
                                            >
                                                {item.day}
                                            </Typography>
                                            <Typography
                                                sx={{ 
                                                    fontFamily: '"Lato", sans-serif', 
                                                    fontSize: '0.8rem', 
                                                    color: '#aaa',
                                                    whiteSpace: 'nowrap'
                                                }}
                                            >
                                                {item.time}
                                            </Typography>
                                        </Box>
                                    ))}
                                </Box>
                            </Box>
                        </Stack>
                    </Grid>
                </Grid>

                {/* Copyright */}
                <Box
                    sx={{
                        borderTop: '1px solid #333',
                        mt: 5,
                        pt: 3,
                        textAlign: 'center',
                    }}
                >
                    <Typography
                        sx={{
                            fontFamily: '"Lato", sans-serif',
                            fontSize: '0.8rem',
                            color: '#666',
                        }}
                    >
                        © 2026 Splash Beauty Lounge. All rights reserved.
                    </Typography>
                </Box>
            </Box>
        </Box>
    );
}